import styled from '@emotion/styled'
import WarningIcon from '@mui/icons-material/Warning'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../'
import { logout } from '../actions/api'
import { fetchBusDriverSignOnsAllowedAction } from '../actions/bus-driver-sign-ons'
import CalendarButton from '../components/calendar/CalendarButton'
import Link from '../components/Link'
import Page from '../components/page/Page'
import Text from '../components/Text'
import config from '../config'
import Info from '../icons/Info'
import Logo from '../icons/Logo'
import Logout from '../icons/Logout'
import Notification from '../icons/Notification'
import Statistics from '../icons/Statistics'
import { openUrl } from '../lib/pwa'
import {
  createRoleSelector,
  hideCalendarLink,
  isOriginalAdmin,
  pushAllowedSelector,
} from '../lib/roleUtils'
import { visitURL } from '../lib/url'
import { selectCanReadBusDriverSignOns } from '../reducers/busDriverSignOnsSlice'
import { defaultTextColor, getColor, theme } from '../Theme'

const Container = styled.div`
  box-sizing: border-box;
  ${theme.layout.fluidWidth(theme.maxWidths.column)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sizes.large};
`

const Header = styled.div`
  ${theme.spacing.sides('large')};
  ${theme.spacing.ends('huge')};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: space-between;
  background: ${(p) => getColor(p.theme, ['primaryGreen'], ['nightGray'])};
  color: ${(p) => getColor(p.theme, ['white'], ['grayPink'])};
`

const HeaderContainer = styled.div`
  box-sizing: border-box;
  ${theme.layout.staticWidth(theme.maxWidths.column)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const HeaderTitle = styled.div`
  margin-top: ${theme.spacing.sizes.large};
  ${theme.text('large', 'content', 'bold')};
  color: ${(p) => getColor(p.theme, ['white'], ['grayPink'])};
`

const HeaderVersion = styled.div`
  ${theme.text('large', 'content')};
  color: ${(p) => getColor(p.theme, ['white'], ['grayPink'])};
`

const Row = styled.a`
  text-decoration: none;
  ${theme.spacing.all('large')};
  background: ${(p) => getColor(p.theme, ['white'], ['nightBlack'])};
  color: ${defaultTextColor};
  box-sizing: border-box;
  border-bottom: 1px solid ${(p) => getColor(p.theme, ['grayBlue'], ['nightSeparator'])};
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
`

const ButtonLink = styled(Link)`
  text-decoration: none;
  ${theme.spacing.all('large')};
  background: ${(p) => getColor(p.theme, ['white'], ['nightBlack'])};
  box-sizing: border-box;
  border-bottom: 1px solid ${(p) => getColor(p.theme, ['grayBlue'], ['nightSeparator'])};
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
`

const IconRow = styled(Row)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  cursor: pointer;
`
const RowIcon = styled.div`
  color: ${(p) => getColor(p.theme, ['primaryBlue'], ['primaryBlue'])};
`

const canSeePunctualityStatisticsSelector = createRoleSelector(
  'commuter_driver',
  'commuter_manager',
  'commuter_conductor'
)
const showCustomerFeedbackSelector = createRoleSelector('commuter_driver', 'commuter_manager')

const showSmDisruptionsPageSelector = createRoleSelector('commuter_driver', 'commuter_security')

const OptionsPage = () => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()
  const user = useTypedSelector((state) => state.user)
  const { evolverFeedbackEmail, punctualityStatisticsPowerBiLink } = useTypedSelector(
    (state) => state.configurations.data
  )

  const showDeveloperOptions = user.admin || user.read_admin
  const canSeePunctualityStatistics = useTypedSelector(canSeePunctualityStatisticsSelector)
  const showCustomerFeedback = useTypedSelector(showCustomerFeedbackSelector)
  const showSmDisruptionsPage = useTypedSelector(showSmDisruptionsPageSelector)
  const showActAs = isOriginalAdmin(user)
  const showDeviceManagement = useTypedSelector(pushAllowedSelector)
  const showBusDriverShiftSigning = useTypedSelector(selectCanReadBusDriverSignOns)

  useEffect(() => {
    dispatch(fetchBusDriverSignOnsAllowedAction())
  }, [dispatch])

  const onLogout = () => {
    dispatch(logout)
    openUrl(`${config.apiBaseUrl}/logout`)
  }

  return (
    <Page menu="options">
      <Header>
        <HeaderContainer>
          <Logo height={30} />
          <HeaderTitle>{t('appName')}</HeaderTitle>
          <HeaderVersion>
            {t('versionNumber', { number: config.version, environment: config.environment })}
          </HeaderVersion>
        </HeaderContainer>
      </Header>
      <Container>
        <IconRow data-testid="menu-item-about" onClick={() => dispatch(visitURL('/about'))}>
          <Text>{t('aboutApp')}</Text>
          <RowIcon>
            <Info iconSize="normal" />
          </RowIcon>
        </IconRow>
        {showBusDriverShiftSigning && (
          <IconRow
            data-testid="menu-item-busDriverSignIn"
            onClick={() => dispatch(visitURL('/bus-driver-check-ins'))}
          >
            <Text>{t('busDriverSignIn.pageDescription')}</Text>
          </IconRow>
        )}
        {canSeePunctualityStatistics && punctualityStatisticsPowerBiLink && (
          <IconRow
            data-testid="menu-item-punctualityStatistics"
            href={punctualityStatisticsPowerBiLink}
            target={'_blank'}
          >
            <Text>{t('punctualityStatistics')}</Text>
            <RowIcon>
              <Statistics iconSize="small" />
            </RowIcon>
          </IconRow>
        )}
        <IconRow data-testid="menu-item-logout" onClick={onLogout}>
          <Text>{t('logout')}</Text>
          <RowIcon>
            <Logout iconSize="normal" />
          </RowIcon>
        </IconRow>
        {showCustomerFeedback && (
          <IconRow
            data-testid="menu-item-customerFeedback"
            onClick={() => dispatch(visitURL('/customerFeedback'))}
          >
            <Text>{t('customerFeedback')}</Text>
            <RowIcon>
              <Notification iconSize="normal" />
            </RowIcon>
          </IconRow>
        )}
        {showSmDisruptionsPage && (
          <IconRow
            data-testid="menu-item-smDisruptions"
            onClick={() => dispatch(visitURL('/smDisruptions'))}
          >
            <Text>{t('smDisruption.pageDescription')}</Text>
            <RowIcon>
              <WarningIcon />
            </RowIcon>
          </IconRow>
        )}
        {/* TODO: Clearer visuals for feedback mailto button*/}
        {evolverFeedbackEmail && (
          <IconRow
            data-testid="menu-item-sendFeedback"
            onClick={() => window.location.assign(`mailto:${evolverFeedbackEmail}`)}
          >
            <Text color="primaryBlue">{t('sendFeedbackEmail')}</Text>
          </IconRow>
        )}
        {!hideCalendarLink(user) && <CalendarButton optionsVersion={true} />}
        {showDeviceManagement && (
          <IconRow
            data-testid="menu-item-deviceManagement"
            onClick={() => dispatch(visitURL('/deviceManagement'))}
          >
            <Text>{t('deviceManagement.title')}</Text>
          </IconRow>
        )}
        {showActAs && (
          <IconRow
            data-testid="menu-item-actAsTool"
            onClick={() => dispatch(visitURL('/actAsTool'))}
          >
            <Text>{t('actAsTool.pageDescription')}</Text>
          </IconRow>
        )}

        {showDeveloperOptions && (
          <ButtonLink data-testid="menu-item-developerOptions" to="/dev">
            {t('developerOptions')}
          </ButtonLink>
        )}
      </Container>
    </Page>
  )
}

OptionsPage.displayName = 'OptionsPage'

export default OptionsPage
